import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../contexts/I18nContext";
import api from "../../lib/api";
import { Sparkles, Trophy, Stethoscope, ShieldCheck, Star, ArrowLeft, ArrowRight, Apple, Smartphone, Megaphone } from "lucide-react";

/**
 * Desktop-only luxury hero/landing section.
 * Visible on >= 768px (md breakpoint). Hidden on mobile (where MobileHomeDashboard takes over).
 * Renders ABOVE the mobile dashboard, so desktop visitors see a proper marketing page first,
 * then can scroll to the interactive app shell.
 */
export default function DesktopHero() {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const ArrowIcon = ar ? ArrowLeft : ArrowRight;

  const [stats, setStats] = useState({ users: 20000, animals: 500, evaluations: 1500 });
  useEffect(() => {
    let base = null;
    const load = () => api.get("/public/stats").then((r) => {
      base = { data: r.data, at: Date.now() };
      setStats(r.data);
    }).catch(() => {});
    load();
    const poll = setInterval(load, 120_000);
    // Live tick — matches the mobile dashboard cadence
    const rates = { users: 1 / 8, animals: 1 / 40, evaluations: 1 / 15 };
    const tick = setInterval(() => {
      if (!base) return;
      const secs = (Date.now() - base.at) / 1000;
      setStats({
        users:       base.data.users       + Math.floor(rates.users       * secs),
        animals:     base.data.animals     + Math.floor(rates.animals     * secs),
        evaluations: base.data.evaluations + Math.floor(rates.evaluations * secs),
      });
    }, 2_000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  return (
    <section className="hidden md:block relative w-full overflow-hidden border-b border-[#D4AF37]/15" dir={ar ? "rtl" : "ltr"} data-testid="desktop-hero">
      {/* Background image with gradient overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/hero-landing.png"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{
            background: ar
              ? "linear-gradient(270deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.85) 55%, #0A0A0A 100%)"
              : "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.85) 55%, #0A0A0A 100%)",
          }}
        />
        {/* Gold dust particles */}
        <div className="absolute inset-0 opacity-20 mix-blend-screen pointer-events-none"
             style={{
               background: "radial-gradient(circle at 20% 80%, #D4AF37 0%, transparent 50%), radial-gradient(circle at 80% 20%, #F9E596 0%, transparent 40%)"
             }} />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Left: Headline + CTAs */}
          <div className="lg:col-span-7 xl:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">
              <Sparkles className="h-3.5 w-3.5" />
              {ar ? "أول منصة ذكية في الخليج" : "First AI platform in the Gulf"}
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tight">
              <span className="gold-text">
                {ar ? "ثِقَتُك في" : "Trust your"}
              </span>
              <br />
              <span className="text-white">
                {ar ? "عالم المزاين" : "livestock world"}
              </span>
            </h1>

            <p className="text-lg lg:text-xl text-white/75 max-w-xl leading-relaxed">
              {ar
                ? "تقييم ثقة الرسمي للإبل والخيول والصقور والمواشي — درجة تقييم + شهادة موثقة + سعر تقريبي + رأي بيطري في تقرير واحد."
                : "Thqah Official evaluation for camels, horses, falcons, sheep & goats — score + verified certificate + market price + vet opinion, all in one report."}
            </p>

            {/* Pricing pill */}
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl glass-gold border border-[#D4AF37]/40">
              <Trophy className="h-5 w-5 text-[#D4AF37]" />
              <div>
                <div className="text-xs text-white/55 uppercase tracking-wider">{ar ? "خطة ذهبية" : "Gold plan"}</div>
                <div className="text-lg font-black gold-text">99 {ar ? "ر.س / شهر" : "SAR / month"}</div>
              </div>
              <div className="h-10 w-px bg-white/15 mx-2" />
              <div>
                <div className="text-xs text-white/55 uppercase tracking-wider">{ar ? "بداية مجانية" : "Free trial"}</div>
                <div className="text-lg font-black text-emerald-400">{ar ? "5 تقييمات" : "5 evaluations"}</div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/register"
                className="btn-gold rounded-full px-7 py-3.5 font-black text-sm inline-flex items-center gap-2 hover:scale-[1.02] transition-transform"
                data-testid="hero-cta-register"
              >
                {ar ? "ابدأ التقييم المجاني" : "Start free evaluation"}
                <ArrowIcon className="h-4 w-4" />
              </Link>
              <Link
                to="/how-it-works"
                className="btn-gold-outline rounded-full px-6 py-3.5 font-bold text-sm inline-flex items-center gap-2"
                data-testid="hero-cta-how"
              >
                {ar ? "كيف يعمل" : "How it works"}
              </Link>
            </div>

            {/* App store badges — disabled until store URLs are live */}
            <div className="flex flex-wrap items-center gap-3 pt-4 text-xs text-white/55">
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 bg-black/60 opacity-60 cursor-not-allowed"
                data-testid="hero-appstore-btn"
                aria-label={ar ? "قريباً على App Store" : "Coming soon on App Store"}
              >
                <Apple className="h-5 w-5" />
                <div className="text-start">
                  <div className="text-[9px] uppercase tracking-wider text-white/55">{ar ? "قريباً على" : "Coming soon"}</div>
                  <div className="text-sm font-bold text-white">App Store</div>
                </div>
              </button>
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 bg-black/60 opacity-60 cursor-not-allowed"
                data-testid="hero-playstore-btn"
                aria-label={ar ? "قريباً على Google Play" : "Coming soon on Google Play"}
              >
                <Smartphone className="h-5 w-5" />
                <div className="text-start">
                  <div className="text-[9px] uppercase tracking-wider text-white/55">{ar ? "قريباً على" : "Coming soon"}</div>
                  <div className="text-sm font-bold text-white">Google Play</div>
                </div>
              </button>
            </div>
          </div>

          {/* Right: Stats card */}
          <div className="lg:col-span-5 xl:col-span-6 lg:flex hidden justify-end">
            <div className="w-full max-w-md space-y-4">
              <StatBlock value={`+${stats.users.toLocaleString()}`} label={ar ? "مستخدم نشط" : "Active users"} sub={ar ? "في الخليج" : "across the Gulf"} />
              <div className="grid grid-cols-2 gap-3">
                <StatBlock value={`+${stats.animals.toLocaleString()}`} label={ar ? "حلال موثّق" : "Verified livestock"} accent />
                <StatBlock value="500" label={ar ? "ر.س / تقييم رسمي" : "SAR / official eval"} accent />
              </div>
              <RatingBlock ar={ar} />
            </div>
          </div>
        </div>
      </div>

      {/* Feature row */}
      <div className="border-t border-white/10 bg-black/40 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Feature icon={Sparkles} title={ar ? "تقييم ذكي" : "AI Evaluation"} sub={ar ? "خلال 30 ثانية" : "in 30 seconds"} />
          <Feature icon={ShieldCheck} title={ar ? "تقييم ثقة الرسمي" : "Thqah Official"} sub={ar ? "شهادة + سعر + رأي بيطري" : "Cert + price + vet opinion"} />
          <Feature icon={Stethoscope} title={ar ? "منتج ثقة" : "Thqah Products"} sub={ar ? "أعلاف معتمدة" : "Certified feed"} />
          <Feature icon={Megaphone} title={ar ? "برنامج المؤثرين" : "Influencer program"} sub={ar ? "20% عمولة" : "20% commission"} />
        </div>
      </div>
    </section>
  );
}

function StatBlock({ value, label, sub, accent }) {
  return (
    <div
      className={`rounded-2xl p-5 border ${accent ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/15 to-black/60" : "border-white/15 glass"}`}
    >
      <div className="text-3xl lg:text-4xl font-black gold-text leading-none">{value}</div>
      <div className="text-sm font-bold text-white/90 mt-2">{label}</div>
      {sub && <div className="text-[11px] text-white/55 mt-0.5">{sub}</div>}
    </div>
  );
}

function RatingBlock({ ar }) {
  return (
    <div className="rounded-2xl p-5 border border-emerald-400/30 bg-gradient-to-br from-emerald-400/10 to-black/40 flex items-center gap-4">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} className="h-5 w-5 fill-[#D4AF37] text-[#D4AF37]" />
        ))}
      </div>
      <div className="flex-1">
        <div className="text-xl font-black text-white">4.8 / 5.0</div>
        <div className="text-[11px] text-white/55">{ar ? "تقييم المستخدمين" : "User rating"}</div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3" data-testid="hero-feature">
      <div className="h-10 w-10 rounded-xl inline-flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/30 shrink-0">
        <Icon className="h-5 w-5 text-[#D4AF37]" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-white truncate">{title}</div>
        <div className="text-[11px] text-white/55 truncate">{sub}</div>
      </div>
    </div>
  );
}
